// ABOUTME: Utility functions for formatting dates and times in a user-friendly way
// ABOUTME: Provides relative time formatting (hours ago, days ago) and absolute date formatting

/**
 * Format a date as relative time for recent dates, absolute date for older ones
 * - Just now (0-16 minutes)
 * - X minutes ago (17-59 minutes)  
 * - X hours ago (1-23 hours 59 minutes)
 * - X days ago (1-6 days)
 * - Absolute date (7+ days)
 * 
 * @param dateString - The date string to format (ISO format expected)
 * @param familyTimezone - Optional timezone to calculate relative time from (e.g., 'America/Los_Angeles')
 */
export const formatRelativeTime = (
  dateString: string | null | undefined, 
  familyTimezone?: string
): string => {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();

  // Elapsed time is a pure instant-to-instant diff — it never needs timezone
  // conversion. The previous implementation reinterpreted the family-timezone
  // wall-clock string as if it were UTC to build a fake "now", which shifted
  // every relative-time label by the family's UTC offset.
  const diffMs = now.getTime() - date.getTime();
  
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMinutes <= 16) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else {
    // For dates older than 7 days, show in the appropriate timezone if provided.
    // toLocaleDateString accepts a timeZone option directly — no need to round-trip
    // through a second Date construction, which would reinterpret the formatted
    // string in the browser's own timezone.
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: familyTimezone,
    });
  }
};

/**
 * Format a date as a friendly absolute date
 */
export const formatFriendlyDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a date as a friendly time
 */
export const formatFriendlyTime = (dateString: string): string => {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format a date and time together for display
 */
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format absolute date and time for admin views (no relative formatting)
 * Shows actual date/time instead of "X hours ago"
 */
export const formatAbsoluteDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format EventLocalTime timestamp (already in family local time, no conversion needed)
 * EventLocalTime is stored as family local time with UTC offset, so we display it as-is
 * @param dateString - ISO date string representing family local time
 * @returns Formatted string like "December 1, 2025 at 5:03 PM"
 */
export const formatEventLocalTime = (dateString: string): string => {
  const date = new Date(dateString);

  // Use UTC methods to extract the time as-is (it's already in family timezone)
  const month = date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  // Convert to 12-hour format
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${month} ${day}, ${year} at ${hours}:${minutes} ${ampm}`;
};

/**
 * Format true UTC timestamp (CreatedAt) as a bare clock time, in family timezone.
 * @param dateString - ISO date string in UTC
 * @param familyTimezone - IANA timezone to convert to (e.g., 'Pacific/Honolulu')
 * @returns Formatted string like "3:42 PM" (falls back to browser timezone if familyTimezone is
 * missing or invalid)
 */
export const formatTimeInTimezone = (
  dateString: string,
  familyTimezone?: string
): string => {
  const date = new Date(dateString);

  if (familyTimezone) {
    try {
      return date.toLocaleTimeString('en-US', {
        timeZone: familyTimezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      console.warn(`Invalid timezone provided: ${familyTimezone}. Falling back to browser timezone.`);
    }
  }

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format true UTC timestamp (CreatedAt) with timezone conversion
 * @param dateString - ISO date string in UTC
 * @param familyTimezone - IANA timezone to convert to (e.g., 'Pacific/Honolulu')
 * @returns Formatted string like "December 1, 2025 at 5:03 PM"
 */
export const formatUtcTimeInTimezone = (
  dateString: string,
  familyTimezone?: string
): string => {
  const date = new Date(dateString);

  if (familyTimezone) {
    try {
      // toLocaleString already produces "December 1, 2025 at 12:58 PM" format
      return date.toLocaleString('en-US', {
        timeZone: familyTimezone,
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      console.warn(`Invalid timezone provided: ${familyTimezone}. Falling back to browser timezone.`);
    }
  }

  // Fallback to browser timezone - also produces "at" format
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Convert a time from family timezone to browser timezone for DateTimePicker display
 *
 * Material-UI DateTimePicker always operates in browser timezone. When we need to show
 * a time that was recorded in family timezone (e.g., Hawaii), we must convert it to
 * browser timezone (e.g., Central) so the picker displays the correct wall-clock time.
 *
 * Example: Event at 5:15 PM Hawaii → User in Central sees 9:15 PM in picker
 *
 * @param familyTimeString - ISO string representing family-local time (e.g., "2023-06-15T17:15:00")
 * @param familyTimezone - IANA timezone of the family (e.g., "Pacific/Honolulu")
 * @returns Date object in browser timezone that represents the same wall-clock time
 */
export const convertFamilyTimeToBrowserTime = (
  familyTimeString: string,
  familyTimezone: string
): Date => {
  // Strip timezone suffix if present
  const cleanedTime = familyTimeString.replace('+00:00', '').replace('Z', '');

  // The cleanedTime is "YYYY-MM-DDTHH:mm:ss" representing family local time
  // We need to create a Date object that when displayed in browser TZ shows the equivalent time

  // First, interpret this time AS IF it were family timezone
  // Get the UTC equivalent of this family-local time
  const parts = cleanedTime.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  if (!parts) throw new Error(`Invalid time string: ${familyTimeString}`);

  const [, year, month, day, hour, minute, second] = parts;

  // Create a date string in family timezone and get its UTC equivalent
  const tempDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
  const utcTime = tempDate.toLocaleString('en-US', {
    timeZone: familyTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  // Parse UTC time back to create the proper Date object
  const [datePart, timePart] = utcTime.split(', ');
  const [m, d, y] = datePart.split('/');
  const [h, min, s] = timePart.split(':');

  return new Date(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}T${h}:${min}:${s}`);
};

/**
 * Convert a browser timezone Date back to family timezone string
 *
 * When user selects a time in DateTimePicker (which operates in browser timezone),
 * we need to convert it back to family timezone for storage.
 *
 * Example: User in Central selects 9:15 PM → Converts to 5:15 PM Hawaii
 *
 * @param browserDate - Date object from DateTimePicker (in browser timezone)
 * @param familyTimezone - IANA timezone of the family (e.g., "Pacific/Honolulu")
 * @returns ISO string in family-local time format (e.g., "2023-06-15T17:15:00")
 */
export const convertBrowserTimeToFamilyTime = (
  browserDate: Date,
  familyTimezone: string
): string => {
  // Get the time as it appears in family timezone
  const familyTimeStr = browserDate.toLocaleString('en-US', {
    timeZone: familyTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  // Parse and format as ISO string
  const [datePart, timePart] = familyTimeStr.split(', ');
  const [month, day, year] = datePart.split('/');
  const [hour, minute, second] = timePart.split(':');

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour}:${minute}:${second}`;
};

/**
 * Convert family-local time string to UTC timestamp
 *
 * When validating user input (family-local time) against original recorded time (UTC),
 * we need to convert the family-local time to UTC for comparison.
 *
 * Example: "2025-12-01T15:00:00" (3pm Hawaii) → "2025-12-02T01:00:00.000Z" (1am UTC next day)
 *
 * @param familyLocalTimeStr - ISO string representing family-local time (e.g., "2025-12-01T15:00:00")
 * @param familyTimezone - IANA timezone (e.g., "Pacific/Honolulu")
 * @returns UTC ISO string (e.g., "2025-12-02T01:00:00.000Z")
 */
export const convertFamilyTimeToUtc = (
  familyLocalTimeStr: string,
  familyTimezone: string
): string => {
  // Parse the family-local time components
  const match = familyLocalTimeStr.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  if (!match) {
    throw new Error(`Invalid time string: ${familyLocalTimeStr}`);
  }

  const [, year, month, day, hour, minute, second] = match;

  // Strategy: Format a known UTC time with the family timezone to get the offset,
  // then apply the reverse offset to our family-local time

  // Create a reference date to determine timezone offset
  const referenceUTC = new Date(`${year}-${month}-${day}T12:00:00Z`);

  // Get the offset by seeing how this UTC time appears in family timezone
  const familyTimeStr = referenceUTC.toLocaleString('en-US', {
    timeZone: familyTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  // Parse to get offset
  const [datePart, timePart] = familyTimeStr.split(', ');
  const [fMonth, fDay, fYear] = datePart.split('/');
  const [fHour, fMinute] = timePart.split(':');

  const familyTime = new Date(`${fYear}-${fMonth.padStart(2, '0')}-${fDay.padStart(2, '0')}T${fHour}:${fMinute}:00Z`);
  const offsetMs = referenceUTC.getTime() - familyTime.getTime();

  // Now apply this offset to our target family-local time
  // Treat family-local time as if it were UTC, then add offset
  const targetAsUtc = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
  const actualUtc = new Date(targetAsUtc.getTime() + offsetMs);

  return actualUtc.toISOString();
};